import React, { useState, useEffect, useMemo } from 'react';
import { isNil } from 'lodash';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
    Modal,
    ModalBody,
    ModalCloseBtn,
    ModalContent,
    ModalFooter,
    ModalHeader
} from '@fiverr-private/overlay';
import { logger } from '@fiverr-private/obs';
import { Input, Switch, Textarea } from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { raiseToast } from '@fiverr-private/toast';
import { vacationStatuses } from '../utils/VacationStatus';
import { convertDateValueToTimestamp, getDateValue, startOfTodayInMillis, submitVacation } from '../helper';
import { MAX_TEXT_LENGTH } from './constants';
import classes from './style.module.scss';

const VacationPopup = ({
    startDate: _startDate,
    endDate: _endDate,
    openCancelVacationPopup,
    userId,
    buyerMessage: _buyerMessage,
    allowContact: _allowContact,
    closePopup,
    vacationStatus
}) => {
    const [loading, setLoading] = useState(false);

    const close = (e, data, fromSubmit = false) => {
        closePopup(e, data, fromSubmit);
    };

    const buildDateString = (timestamp) =>
        timestamp && vacationStatus !== vacationStatuses.historic ? getDateValue(timestamp) : '';

    const [startDateString, setStartDateString] = useState(buildDateString(_startDate));
    const [endDateString, setEndDateString] = useState(buildDateString(_endDate));
    const [allowContact, setAllowContact] = useState(_allowContact);
    const [buyerMessage, setBuyerMessage] = useState(isNil(_buyerMessage) ? translate('out_of_office.banner.buyer.seller_disabled_contact') : _buyerMessage);

    const startDate = useMemo(() => convertDateValueToTimestamp(startDateString), [startDateString]);
    const endDate = useMemo(() => convertDateValueToTimestamp(endDateString, true), [endDateString]);

    const onStartDateChange = ({ target: { value } }) => {
        setStartDateString(value);
    };

    const onEndDateChange = ({ target: { value } }) => {
        setEndDateString(value);
    };

    const startOfTodayDateValue = getDateValue(startOfTodayInMillis());

    const onAllowContactToggle = () => {
        setAllowContact((checked) => !checked);
    };

    const onBuyerMessageChange = ({ target: { value } }) => {
        setBuyerMessage(value);
    };

    useEffect(() => {
        if (startDate > 0 && endDate > 0 && startDate > endDate) {
            // Reset end date if user selected a bigger start date.
            setEndDateString('');
        }
    }, [endDate, startDate]);

    const submit = async() => {
        const payload = {
            start_date: Math.floor(startDate / 1000),
            end_date: Math.floor(endDate / 1000),
            message_for_buyer: buyerMessage,
            allow_contact: allowContact,
            reason: '',
            userId
        };

        try {
            setLoading(true);
            const { status, message: errorMessage } = await submitVacation(payload);
            setLoading(false);

            if (status === 'success') {
                const immediateVacation = new Date().getTime() >= startDate;

                const state = {
                    startDate,
                    endDate,
                    buyerMessage: payload.message_for_buyer,
                    allowContact: payload.allow_contact,
                    vacationStatus: immediateVacation ? vacationStatuses.active : vacationStatuses.pending
                };

                const event = new CustomEvent('out_of_office_on', { detail: state });
                document.dispatchEvent(event);

                closePopup(null, state, true);

                raiseToast({
                    message: <I18n k="out_of_office.vacation_popup.success_toast"/>,
                    location: 'top',
                    type: 'success'
                });
            } else {
                throw new Error(errorMessage);
            }
        } catch (err) {
            setLoading(false);
            logger.error(err, {
                description: 'Failed to submit vacation',
                userId,
                payload
            });

            raiseToast({
                message: <I18n k="out_of_office.vacation_popup.error_toast"/>,
                location: 'top',
                type: 'error'
            });
        }
    };

    const formIsIncomplete = !startDate || !endDate;

    return (
        <Modal isOpen={true} onClose={close}>
            <ModalContent size="sm">
                <ModalHeader>
                    <section className="flex flex-justify-between flex-items-center">
                        {
                            vacationStatus === vacationStatuses.historic ?
                                <I18n k="out_of_office.vacation_popup.schedule_title"/> :
                                <I18n k="out_of_office.vacation_popup.edit_availability_title"/>
                        }
                        <ModalCloseBtn/>
                    </section>
                </ModalHeader>
                <ModalBody>
                    <section className={classNames('flex flex-col', classes.content)}>
                        <p>
                            <I18n k="out_of_office.vacation_popup.subtitle"/>
                        </p>
                        <section>
                            <label className={classes.fLabel}>
                                <I18n k="out_of_office.vacation_popup.choose_dates"/>
                            </label>
                            <section className={classNames('flex flex-col', classes.datesContainer)}>
                                <section>
                                    <label className={classes.fLabel}>
                                        <I18n k="out_of_office.vacation_popup.first_day"/>
                                    </label>
                                    <Input min={startOfTodayDateValue} disabled={vacationStatus === vacationStatuses.active} value={startDateString} onChange={onStartDateChange} type="date"/>
                                </section>
                                <section>
                                    <label className={classes.fLabel}>
                                        <I18n k="out_of_office.vacation_popup.last_day"/>
                                    </label>
                                    <Input min={startDateString} value={endDateString} onChange={onEndDateChange} type="date"/>
                                </section>
                            </section>
                        </section>
                        <section className={classes.contactSettingsContainer}>
                            <div className={classes.label}>
                                <label className={classes.fLabel}>
                                    <I18n k="out_of_office.vacation_popup.buyer_can_contact"/>
                                </label>
                            </div>
                            <p className={classNames('m-b-16', classes.subtitle)}>
                                <I18n k="out_of_office.vacation_popup.enable_if_you_can"/>
                            </p>
                            <div className={classes.switch}>
                                <Switch checked={allowContact} onChange={onAllowContactToggle} size="sm"/>
                            </div>
                        </section>
                        <section>
                            <label className={classes.fLabel}>
                                <I18n k="out_of_office.vacation_popup.add_message"/>
                                {' '}
                                <small>
                                    <I18n k="out_of_office.vacation_popup.optional"/>
                                </small>
                            </label>
                            <p className="m-b-16">
                                <I18n k="out_of_office.vacation_popup.buyers_see_your_message"/>
                            </p>
                            <Textarea className={classes.textArea}
                                maxLength={MAX_TEXT_LENGTH}
                                value={buyerMessage}
                                onChange={onBuyerMessageChange}/>
                        </section>
                    </section>
                </ModalBody>
                <ModalFooter>
                    {
                        vacationStatus === vacationStatuses.pending ? (
                            <Button variant="ghost" intent="secondary" onClick={openCancelVacationPopup}>
                                <I18n k="out_of_office.vacation_popup.delete_pending_vacation"/>
                            </Button>
                        ) : (
                            <Button variant="ghost" intent="secondary" onClick={close}>
                                <I18n k="out_of_office.general.cancel"/>
                            </Button>
                        )
                    }
                    <Button intent="primary" onClick={submit} loading={loading} disabled={loading || formIsIncomplete}>
                        {
                            vacationStatus === vacationStatuses.historic ?
                                <I18n k="out_of_office.vacation_popup.confirm_dates"/> :
                                <I18n k="out_of_office.general.save_changes"/>
                        }
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

VacationPopup.propTypes = {
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    userId: PropTypes.number,
    buyerMessage: PropTypes.string,
    allowContact: PropTypes.bool,
    closePopup: PropTypes.func.isRequired,
    openCancelVacationPopup: PropTypes.func,
    vacationStatus: PropTypes.string
};

VacationPopup.defaultProps = {
    buyerMessage: '',
    allowContact: false,
    closePopup: () => null,
    openCancelVacationPopup: () => null,
    vacationStatus: vacationStatuses.historic
};

export default VacationPopup;
